import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import Header from "@/components/Header";
import PublicFooter from "@/components/PublicFooter";
import { BookOpen, FileText, UserCheck, TrendingUp, ArrowRight } from "lucide-react";

const guides = [
  {
    slug: "interview-preparation",
    title: "Complete Interview Preparation Guide",
    description: "Master the art of interview preparation with our comprehensive step-by-step guide covering everything from research to follow-up.",
    icon: BookOpen,
    readTime: "12 min read",
    category: "Fundamentals"
  },
  {
    slug: "job-description-analysis",
    title: "How to Analyze a Job Description Like a Recruiter",
    description: "Learn to decode job descriptions the way recruiters do. Understand hidden requirements and position yourself as the ideal candidate.",
    icon: FileText,
    readTime: "10 min read",
    category: "Strategy"
  },
  {
    slug: "senior-interview-questions",
    title: "Senior-Level Interview Questions & Strategies",
    description: "Prepare for executive and senior-level interviews. Learn how to demonstrate leadership, strategic thinking, and business impact.",
    icon: UserCheck,
    readTime: "15 min read",
    category: "Advanced"
  }
];

const GuidesIndexPage = () => {
  return (
    <>
      <Helmet>
        <title>Interview Guides & Career Resources | Hireun</title>
        <meta name="description" content="Free interview preparation guides and career resources. Learn how to ace your next interview with expert strategies and proven techniques." />
        <meta name="keywords" content="interview preparation, career guides, job interview tips, interview questions, career advice" />
        <link rel="canonical" href="https://hireun.com/guides" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <Header />

        {/* Hero Section */}
        <section className="hero-gradient py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-4 text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Interview Guides & Resources
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Expert strategies and proven techniques to help you succeed in your next interview. 
                Free resources backed by real hiring insights.
              </p>
            </div>
          </div>
        </section>

        {/* Guides Grid */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {guides.map((guide) => (
                <Link
                  key={guide.slug}
                  to={`/guides/${guide.slug}`}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-lg transition-shadow group"
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs font-medium text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                      {guide.category}
                    </span>
                    <span className="text-xs text-gray-500">{guide.readTime}</span>
                  </div>
                  
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                    <guide.icon className="w-6 h-6 text-blue-600" />
                  </div>
                  
                  <h2 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                    {guide.title}
                  </h2>
                  
                  <p className="text-gray-600 text-sm mb-4">
                    {guide.description}
                  </p>
                  
                  <div className="flex items-center text-blue-600 text-sm font-medium">
                    Read Guide
                    <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Why Read Our Guides */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Why Our Guides Are Different
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Written by career experts with real hiring experience, not generic advice.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Data-Driven Insights</h3>
                <p className="text-gray-600">
                  Our advice is based on real hiring data and feedback from thousands of interviews.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <UserCheck className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Recruiter Perspective</h3>
                <p className="text-gray-600">
                  Learn what recruiters and hiring managers actually look for in candidates.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <BookOpen className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Actionable Steps</h3>
                <p className="text-gray-600">
                  Every guide includes concrete steps you can implement immediately.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gray-900 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold mb-4">
              Ready for Personalized Interview Prep?
            </h2>
            <p className="text-lg text-gray-300 mb-8">
              Our guides give you the foundation. Hireun gives you personalized preparation 
              tailored to your specific role and experience.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center bg-blue-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Try Hireun Free
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </section>

        <PublicFooter />
      </div>
    </>
  );
};

export default GuidesIndexPage;
